/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import {
  CapDataDesc,
  DefaultCaps,
  Director,
  Stage,
  StageDesc,
  PointsMaterialShape,
  Text2DFontLoader,
  DefaultNavigators,
  CapNames,
  SceneCompass,
} from '@sick-davinci/glow';
import {
  CrownPointCloudDesc,
  CrownViewCapData,
  CrownPointCloudCap,
  CrownShapeCap,
  CrownTextCap,
  CrownPointCloudDecorationDesc,
  CrownViewPresentAddDesc,
} from '@sick-davinci/glow-crown';
import {
  CrownConnectionWebSocket,
  CrownProtocolMsgPack,
  LogLevel,
  MsgPackDecoder,
  CrownDataDesc,
} from '@sick-davinci/device-communication';

import { addStatsWidget } from '@sick-davinci/glow-utils';
import { FileLoader } from 'three';

import { DropDown, TextField, Slider, NumericField } from '@sick-davinci/basic-elements';

export const dependencies = [DropDown, TextField, Slider, NumericField];  // avoid wepack removal

/**
 * Demo:    CrownPointCloudViewer
 * Description:
 *  This demo application displays a CrownPointCloud.
 *  Incoming (using Crown API / CrownView - Iface) device data (PointClouds and Shapes) is put on the scene.
 *  Use the UI elements to switch the presentation of the pointcloud.
 * Features:
 *  - Orbit and 1st person navigation
 *  - Capability handling
 *  - Scene compass
 *  - ViewCrown / WebSocket connection
 *  - Overwrite device decoration
 */
export const run = (async (viewParent: HTMLElement) => {
  // ** setup the stage *
  const setup: StageDesc = {
    cameras: [
      {
        name: 'default',
        from: [0.1, 0, -200],
        to: [-1, 0, 0],
        up: [0, 1, 0],
        fov: 60,
      },
    ],
    scenes: [{
      name: 'default',
      }],
    viewports: [
      {
        name: 'default',
        scene: 'default',
        camera: 'default',
        navigator: 'orbit',
        left: 0,
        top: 0,
        width: 1.0,
        height: 1.0,
        bgcolor: 0x007fc3,
      },
    ],
  };

  const stage = new Stage(viewParent, setup);

  // * Add Navigators
  const navOrbit = DefaultNavigators.initOrbit(stage, 'orbit', {pan: { speed: 5 }});  // activated per default
  const nav1stP = DefaultNavigators.initFirstPerson(stage, 'firstPerson', {pan: { speed: 5 }});  // alternative navigator, selected via drop-down

  // * Add the scene compass for orientation
  SceneCompass.create(stage, 'default', 'default');

  // ** add fps-meter to the scene
  addStatsWidget(stage);
  stage.start();  // start visualizing data

  let lastPointCloud: CapDataDesc<CrownViewPresentAddDesc<CrownPointCloudDesc, CrownPointCloudDecorationDesc>>;

  // ** data handling using capabilities *
  const director = new Director(stage);
  DefaultCaps.init(director); // addCapability for all default caps

  // Add Crown caps
  director.addCapability(await createTextCap());
  const pointCloudCap = new CrownPointCloudCap(); // accessed below to change point change
  director.addCapability(pointCloudCap);
  director.addCapability(new CrownShapeCap());

  // * Add some light so there is smth to see
  director.handle(CapNames.LIGHT, { payload: { lightType: 'spot', color: 0x777777 }, orientation: { position: [10, 10, 0] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0x777777 } });

  // * Add lookat axis
  director.handle(CapNames.AXIS, { axisLength: 30 });

  // ** Connect to the device via the ViewCrown using the provided ip and view-id
  const connection = new CrownConnectionWebSocket('127.0.0.1', 'crownMSGPACK', 3000);
  const protocol = new CrownProtocolMsgPack();
  const view = new CrownViewCapData(connection, protocol, '3D');

  // ** Set all necessary callback functions for the device connection
  view.onPresentStart(() => stage.lockRenderer());
  view.onPresentEnd(() => stage.unlockRenderer());
  view.onPresentClear(() => stage.clearScene('default'));
  view.onPresentRemove((id) => stage.clearObject(stage.getObjectIdByName(id)));
  view.onPresentAddCapData((capData: CapDataDesc<CrownViewPresentAddDesc<CrownPointCloudDesc, CrownPointCloudDecorationDesc>>) => {
    if (capData.payload.Type === CrownPointCloudCap.className) {
      lastPointCloud = capData; // remember so it can be reapplied after changing presentation
    }

    director.handleCapData(capData);
  });

  // ** Send some data as soon as connection is established
  view.onConnectionChanged((connected) => {
    if (connected) {
       view.logMessage(LogLevel.INFO, 'PointCloud Viewer connected.');
    }
  });

  // ** load an offline point-cloud first
  const loader = new FileLoader();
  const msgPackDecoder = new MsgPackDecoder();

  loader.setResponseType('arraybuffer');
  loader.load('./resources/hand_spread.msgpack', (buf) => {
    const pointCloudDesc = msgPackDecoder.decode(buf as any) as CrownDataDesc<CrownPointCloudDesc>;
    const capData: CapDataDesc<CrownViewPresentAddDesc<CrownPointCloudDesc, CrownPointCloudDecorationDesc>> = {
      payload: {
        Type: CrownPointCloudCap.className,
        Iconics: [pointCloudDesc],
        Decorations: [getDecoration()],
      },
      name: 'hand',
      parent: '',
    };

    lastPointCloud = capData;
    director.handleCapData(capData);
  });

  // ** GUI **
  let colorMapId = parseInt((document.getElementById('colormap') as DropDown).value, 10);
  let colorMapAttribute = (document.getElementById('colormapAttribute') as DropDown).value;
  let pointSize = (document.getElementById('size') as NumericField).value;
  const colorMapDropDown = document.getElementById('colormap') as DropDown;
  colorMapDropDown.addEventListener('change', ((evt) => {
    colorMapId = parseInt((evt as any).detail, 10);
    if (lastPointCloud != null) {
      lastPointCloud.payload.Decorations[0] = getDecoration();
      director.handleCapData(lastPointCloud);
    }
  }), true);

  const colorMapAttributeDropDown = document.getElementById('colormapAttribute') as DropDown;
  colorMapAttributeDropDown.addEventListener('change', ((evt) => {
    colorMapAttribute = (evt as any).detail;
    if (lastPointCloud != null) {
      lastPointCloud.payload.Decorations[0] = getDecoration();
      director.handleCapData(lastPointCloud);
    }
  }), true);

  const shapeDropDown = document.getElementById('shape') as DropDown;
  shapeDropDown.addEventListener('change', ((evt) => {
    if (lastPointCloud != null) {
      pointCloudCap.setPointShape((evt as any).detail as PointsMaterialShape);
      director.handleCapData(lastPointCloud);
    }
  }), true);

  const sizeField = document.getElementById('size') as TextField;
  sizeField.addEventListener('change', ((evt) => {
    pointSize = parseInt((evt as any).detail, 10);
    if (lastPointCloud != null) {
      lastPointCloud.payload.Decorations[0] = getDecoration();
      director.handleCapData(lastPointCloud);
    }
  }), true);

  const navigationDropDown = document.getElementById('navigation') as DropDown;
  navigationDropDown.parentElement.addEventListener('change', ((evt) => {
    stage.changeNavigator((evt as any).detail, 'default');
  }), true);
  stage.changeNavigator(navigationDropDown.value, 'default');

  const getDecoration = () => {
    return {
      class: CrownPointCloudCap.decoClassName,
      data: {
        IColorMap: colorMapId,
        IMax: 1,
        IMin: 0,
        IRangeSet: colorMapAttribute === 'intensity' ? true : false,
        PointSize: pointSize,
        XColorMap: colorMapId,
        XMax: 1000,
        XMin: 0,
        XRangeSet: colorMapAttribute === 'x' ? true : false,
        YColorMap: colorMapId,
        YMax: 1000,
        YMin: 0,
        YRangeSet: colorMapAttribute === 'y' ? true : false,
        ZColorMap: colorMapId,
        ZMax: 1000,
        ZMin: 0,
        ZRangeSet: colorMapAttribute === 'z' ? true : false,
      },
    } as CrownDataDesc<CrownPointCloudDecorationDesc>;
  };

  function setZoomSpeed(speed: number) {
    navOrbit.straightAhead.configure({wheelTouchFactor: 3 * speed, mousePanFactor: 3000 * speed});
    nav1stP.straightAhead.configure({wheelTouchFactor: 3 * speed, mousePanFactor: 3000 * speed});
  }
  const zoomSpeed = document.getElementById('zoomSpeed') as Slider;
  zoomSpeed.addEventListener('change', ((evt) => {
    const val = (evt as any).detail as number;
    setZoomSpeed(val);
  }));
  setZoomSpeed(zoomSpeed.value);
});

// * create text cap with necessary font resources *
async function createTextCap() {
  const baseUrl = './resources/Text2D/';
  const fonts = {
    SANSSERIF: await Text2DFontLoader.getFont(baseUrl + 'OpenSans-Regular.json', baseUrl),
    SERIF: await Text2DFontLoader.getFont(baseUrl + 'NotoSerifJP-Regular.json', baseUrl),
    MONOSPACE: await Text2DFontLoader.getFont(baseUrl + 'CutiveMono-Regular.json', baseUrl),
  };
  return new CrownTextCap(fonts);
}
